/**
 * **Das Tonband des Rekorders** (Sascha, 15. Oktober: „VoiceOver
 * verarbeitet leider nicht … Können wir das ein bisschen mehr machen wie den
 * Apple Sprachrekorder? Ich kann dann anhören, zurückspulen, ersetzen,
 * kürzen … also Vollbildfenster mit guter Bearbeitungsmöglichkeit“).
 *
 * Aufgenommen wird nicht mehr über den `MediaRecorder` des Browsers, sondern
 * als rohe Abtastwerte: so lässt sich jede Stelle anhören, überschreiben und
 * herausschneiden, ohne erst etwas zu dekodieren -- und ohne das Format, das
 * jeder Browser anders kann. Erst „Einfügen“ macht daraus eine Datei (WAV,
 * 16 Bit), die wie jede andere hochgeladen und aufbereitet wird.
 *
 * Hier steht nur Rechnen auf den Werten; die Bedienung steht im Rekorder.
 * Alle Stellen sind Abtastwerte, nicht Sekunden: das Tonband weiss nichts von
 * Zeit, nur der Rekorder kennt die Rate.
 */
/** Viele Stücke, wie sie beim Aufnehmen ankommen, zu einem. */
export function zusammen(stuecke) {
    let laenge = 0;
    for (const s of stuecke)
        laenge += s.length;
    const aus = new Float32Array(laenge);
    let pos = 0;
    for (const s of stuecke) {
        aus.set(s, pos);
        pos += s.length;
    }
    return aus;
}
function klemme(stelle, laenge) {
    return Math.max(0, Math.min(laenge, Math.round(stelle)));
}
/**
 * **Ersetzen** wie im Sprachrekorder: ab `ab` wird überschrieben, so lang wie
 * das Neue ist. Was danach kommt, bleibt; ist das Neue länger als der Rest,
 * wird das Band länger.
 */
export function ersetzeAb(alt, neu, ab) {
    const von = klemme(ab, alt.length);
    const ende = Math.max(alt.length, von + neu.length);
    const aus = new Float32Array(ende);
    aus.set(alt.subarray(0, von), 0);
    aus.set(neu, von);
    if (von + neu.length < alt.length)
        aus.set(alt.subarray(von + neu.length), von + neu.length);
    return aus;
}
/** **Kürzen**: nur das Stück zwischen `von` und `bis` bleibt. */
export function behalte(alt, von, bis) {
    const a = klemme(Math.min(von, bis), alt.length);
    const b = klemme(Math.max(von, bis), alt.length);
    return alt.slice(a, b);
}
/** **Löschen**: das Stück zwischen `von` und `bis` fällt heraus, der Rest schliesst an. */
export function loesche(alt, von, bis) {
    const a = klemme(Math.min(von, bis), alt.length);
    const b = klemme(Math.max(von, bis), alt.length);
    const aus = new Float32Array(alt.length - (b - a));
    aus.set(alt.subarray(0, a), 0);
    aus.set(alt.subarray(b), a);
    return aus;
}
/**
 * Die Wellenform zum Zeichnen: je Eimer die grösste Auslenkung, 0…1. Ein
 * leeres Band hat keine Eimer.
 */
export function spitzenVon(werte, eimer) {
    const n = Math.max(0, Math.floor(eimer));
    if (werte.length === 0 || n === 0)
        return [];
    const aus = new Array(n).fill(0);
    const breite = werte.length / n;
    for (let i = 0; i < n; i++) {
        const a = Math.floor(i * breite);
        const b = Math.max(a + 1, Math.floor((i + 1) * breite));
        let hoch = 0;
        for (let j = a; j < b && j < werte.length; j++) {
            const v = Math.abs(werte[j]);
            if (v > hoch)
                hoch = v;
        }
        aus[i] = Math.min(1, hoch);
    }
    return aus;
}
/**
 * Das Band als WAV-Datei: ein Kanal, 16 Bit. Ohne Kompression -- die macht
 * danach der Arbeitsdienst wie bei jeder anderen Datei, und was hier verloren
 * ginge, wäre für immer weg.
 */
export function alsWav(werte, rate) {
    const daten = werte.length * 2;
    const puffer = new ArrayBuffer(44 + daten);
    const v = new DataView(puffer);
    const text = (pos, s) => {
        for (let i = 0; i < s.length; i++)
            v.setUint8(pos + i, s.charCodeAt(i));
    };
    text(0, 'RIFF');
    v.setUint32(4, 36 + daten, true);
    text(8, 'WAVE');
    text(12, 'fmt ');
    v.setUint32(16, 16, true);
    v.setUint16(20, 1, true); // PCM
    v.setUint16(22, 1, true); // ein Kanal
    v.setUint32(24, Math.round(rate), true);
    v.setUint32(28, Math.round(rate) * 2, true);
    v.setUint16(32, 2, true);
    v.setUint16(34, 16, true);
    text(36, 'data');
    v.setUint32(40, daten, true);
    for (let i = 0; i < werte.length; i++) {
        const s = Math.max(-1, Math.min(1, werte[i]));
        v.setInt16(44 + i * 2, s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff), true);
    }
    return new Uint8Array(puffer);
}
/** Zeit auf dem Band, wie der Rekorder sie zeigt: „0:07,4“ oder „1:02:05,0“. */
export function bandZeit(sekunden) {
    const s = Math.max(0, sekunden);
    const zehntel = Math.floor(s * 10) % 10;
    const ganz = Math.floor(s);
    const std = Math.floor(ganz / 3600);
    const min = Math.floor((ganz % 3600) / 60);
    const sek = ganz % 60;
    const mm = std > 0 ? String(min).padStart(2, '0') : String(min);
    return `${std > 0 ? `${std}:` : ''}${mm}:${String(sek).padStart(2, '0')},${zehntel}`;
}
//# sourceMappingURL=tonband.js.map